import * as Yup from "yup";
import {
  FieldTypes,
  IFieldInstance,
} from "../../types/fieldInstance.interface";
import {
  extractNumber,
  getRLFormFields,
  getRLType,
  incidentRelatedLists,
} from "../helpers/relatedListHelpers";
import { fieldTypes } from "./getInitialValues";
import { findFormfieldById, getFormFieldsByProperty } from "./hashTable";
import { getFieldMaxLength } from "./fieldMaxLengths";
import {
  getConstraint,
  getConstraintDateString,
  isWithinSupportedDateRange,
  UNSUPPORTED_DATE_RANGE_MESSAGE,
} from "../../components/Forms/dateTimeInput/methods";

export const valueIsObject = (value: any) => {
  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    return true;
  } else {
    return false;
  }
};

const getYupFieldSchema = (
  fieldType: keyof typeof fieldTypes,
  fieldName: string
) => {
  if (fieldTypes[fieldType] === undefined) {
    throw new Error(`Invalid field type: ${fieldType}`);
    // should this return undefined?
  }
  if (typeof fieldTypes[fieldType] === "string") {
    return Yup.string();
  } else if (Array.isArray(fieldTypes[fieldType])) {
    if (Object.values(incidentRelatedLists).includes(fieldName)) {
      return Yup.array().of(Yup.object());
    } else {
      return Yup.array();
    }
  } else if (valueIsObject(fieldTypes[fieldType])) {
    return Yup.object();
  } else if (typeof fieldTypes[fieldType] === "boolean") {
    return Yup.boolean();
  } else if (fieldType === "Date/Time") {
    return Yup.date().nullable().default(null);
  }
};

// stronger typing

const getToggleBoxDetails = (
  field: IFieldInstance,
  rlFieldName?: string
): IFieldInstance | null => {
  const toggleBoxChildren = [];
  if (field.childFieldIds.length) {
    toggleBoxChildren.push(
      ...getFormFieldsByProperty(
        "fieldType",
        "Togglebox",
        rlFieldName,
        undefined,
        field.childFieldIds
      )
    );
  }
  return (toggleBoxChildren[0] as IFieldInstance) || null;
};

const getSchemaCondition = (field: IFieldInstance, rlFieldName?: string) => {
  const schemaCondition: any[] = [];
  const toggleBoxDetails = getToggleBoxDetails(field, rlFieldName);
  // extend to handle other cases beyond togglebox
  if (toggleBoxDetails) {
    schemaCondition.push(toggleBoxDetails.fieldName);
  }
  return schemaCondition;
};

const getConditionCheckFunction = (
  viewModel: any,
  field: any,
  rlFieldName?: string
) => {
  const schemaCondition = getSchemaCondition(field, rlFieldName);
  if (schemaCondition.length) {
    return (val: boolean) => val === false && viewModel[field.id];
  } else return () => viewModel[field.id];
};

const getValidationForRequired = (
  yupFieldSchema: any,
  field: IFieldInstance,
  viewModel: any,
  rlFieldName?: string
) => {
  let requiredFieldSchema = yupFieldSchema.required("This field is required");
  const isFieldArray = field.fieldType === FieldTypes.RelatedList;
  if (
    Array.isArray(fieldTypes[field.fieldType as keyof typeof fieldTypes]) &&
    !isFieldArray
  ) {
    requiredFieldSchema = viewModel?.[field.id]
      ? requiredFieldSchema.min(1, "Select at least one option")
      : requiredFieldSchema;
  } else if (isFieldArray) {
    requiredFieldSchema = viewModel?.[field.id]
      ? requiredFieldSchema.min(1, "Add at least one")
      : requiredFieldSchema.min(0).default([]);
  }

  const requiredFieldValidation = yupFieldSchema.when(
    getSchemaCondition(field, rlFieldName),
    {
      is: getConditionCheckFunction(viewModel, field, rlFieldName),
      then: requiredFieldSchema,
      otherwise: yupFieldSchema.notRequired(),
    }
  );
  if (field.fieldType === "Lookup" || field.fieldType === "Picklist") {
    const togglebox = getToggleBoxDetails(field, rlFieldName);
    // add rl check here
    return viewModel?.[field.id] && togglebox
      ? requiredFieldValidation.test(
          "should-enter-text",
          "Enter text",
          (_: any, values: any) => {
            const toggleboxChild = findFormfieldById(
              togglebox.childFieldIds[0],
              rlFieldName
            );
            if (
              values.parent[togglebox.fieldName] &&
              toggleboxChild &&
              toggleboxChild.fieldType === "Text"
            ) {
              return !values.parent[toggleboxChild.fieldName] ? false : true;
            } else return true;
          }
        )
      : requiredFieldValidation;
  }
  return requiredFieldValidation;
};

const extendYupFieldSchema = (yupFieldSchema: any, field: IFieldInstance) => {
  // extend numberString schema for pulse and bp
  let extendedYupSchema = yupFieldSchema;
  if (field.fieldType === "TextWithLabel") {
    extendedYupSchema = extendedYupSchema.test(
      "is-input-number",
      "Please input a number",
      function (this: Yup.TestContext<any>, value: string) {
        const { path, createError } = this;
        if (!this.parent[path] || !this.parent[path].length) {
          return true; // skip validation if the field hasn't been touched
        }
        const inputVal = value ? value.split(",") : [""];
        const isNumeric =
          !isNaN(Number(inputVal[0])) && !isNaN(parseFloat(inputVal[0]));
        if (isNumeric || inputVal[0].trim() === "") {
          return true;
        }
        return createError({ path, message: "Please input a number" });
      }
    );
    const isOptionsRequired = false;
    extendedYupSchema =
      field.values.length && isOptionsRequired
        ? extendedYupSchema.matches(
            new RegExp(`^.*,\\s(${field.values.join("|")})$|^$`),
            "Please select one option"
          )
        : extendedYupSchema;
  }
  if (field.fieldType === FieldTypes.DateTime) {
    extendedYupSchema = extendedYupSchema.test(
      "is within time range",
      "Time entry cannot be in the future",
      function (this: Yup.TestContext<any>, value: string) {
        const { path, createError } = this;
        if (!this.parent[path]) {
          return true; // skip validation if the field hasn't been touched
        }
        let diff;
        if (field?.constraint) {
          const dateConstraint = getConstraint(
            field.constraint,
            this.parent?.["CreatedDate"],
            this.parent?.["Incident_Date_Time__c"]
          );
          const dateConstraintValues = dateConstraint && {
            min: getConstraintDateString(dateConstraint, "min"),
            max: getConstraintDateString(dateConstraint, "max"),
          };
          const timeEntered = new Date(value);
          const max =
            dateConstraintValues?.max && new Date(dateConstraintValues?.max);
          diff = max && timeEntered.getTime() - max.getTime();
        }
        if (typeof diff === "number" && diff > 0) {
          return createError({
            path,
            message: "Time entry cannot be in the future",
          });
        } else {
          return true;
        }
      }
    );
    // Hard guardrail: reject dates outside the supported range — older than 5
    // years (MARCC-689 business rule) or above Salesforce's year-4000 ceiling.
    // Catches a mistyped year (e.g. "0026") regardless of how it was entered —
    // typed, pasted, or spun — and surfaces an inline error so the user fixes it
    // before submit instead of hitting a FIELD_INTEGRITY_EXCEPTION at the API.
    //
    // IMPORTANT: validate `this.originalValue` (the raw ISO string the form
    // stored), NOT a cast value. `Yup.date()` casts the string to a Date first,
    // and its parser runs a 2-digit year through `Date.UTC`, which pivots "0026"
    // to 1926 — a different year than the user entered, and one a pivot could in
    // principle land back inside the supported window. `new Date()` on the
    // original string reads the true year (26), so the bad value is caught.
    extendedYupSchema = extendedYupSchema.test(
      "is-within-supported-datetime-range",
      UNSUPPORTED_DATE_RANGE_MESSAGE,
      function (this: Yup.TestContext<any>) {
        // `originalValue` (the raw pre-cast string) exists at runtime but isn't
        // on Yup's TestContext type, hence the cast.
        const originalValue = (
          this as unknown as { originalValue?: string | null }
        ).originalValue;
        return isWithinSupportedDateRange(originalValue);
      }
    );
  }
  // Safety net for the native maxLength attribute (which silently truncates,
  // including on paste): cap free-text fields at the Salesforce field length so
  // any over-length value that bypasses the attribute (e.g. a programmatic set)
  // surfaces an inline error instead of failing at the Apex upsert
  // (STRING_TOO_LONG, MARCC-690). A falsy cap (incl. 0) intentionally skips
  // enforcement rather than forcing the field empty.
  if (field.fieldType === "Text" || field.fieldType === "TextArea") {
    const max = getFieldMaxLength(field.fieldName);
    if (max) {
      extendedYupSchema = extendedYupSchema.max(
        max,
        `Cannot exceed ${max} characters`
      );
    }
  }
  return extendedYupSchema;
};

export const getStaticShape = (
  fields: IFieldInstance[],
  requiredFields: IFieldInstance[]
) => {
  const staticShape: any = {};
  fields.forEach((field) => {
    let schema = getYupFieldSchema(
      field.fieldType as keyof typeof fieldTypes,
      field.fieldName
    );
    if (
      requiredFields.some(
        (item) => JSON.stringify(item) === JSON.stringify(field)
      )
    ) {
      schema =
        schema?.type === "array"
          ? (schema as any).min(1, "Select at least one option")
          : schema?.required("This field is required");
    }
    staticShape[field.fieldName] = schema;
  });
  return staticShape;
};

export const getYupValidator = (
  fields: IFieldInstance[],
  viewModel: any,
  rlViewModel?: any,
  rlFieldName?: string,
  isSubmittedIncident: boolean = false
) => {
  return fields?.reduce((acc: any, current) => {
    // Fields flagged requiredForNewIncidentOnly drop their required rule once the
    // incident is already submitted, so historical sign-off isn't blocked (MARCC-772).
    const enforceRequired =
      current.required &&
      !(isSubmittedIncident && current.requiredForNewIncidentOnly);
    let yupFieldSchema = getYupFieldSchema(
      current.fieldType as keyof typeof fieldTypes,
      current.fieldName
    );
    if (Object.values(incidentRelatedLists).includes(current.fieldName)) {
      const fields = getRLFormFields(current.fieldName);
      const _fields = Object.values(fields).flat() as IFieldInstance[];
      const rlType = getRLType(current.fieldName);
      const updatedYupFieldSchema = rlViewModel
        ? Yup.array().of(
            Yup.object().when([], {
              is: true,
              then: Yup.lazy((value, resolveOptions) => {
                const x = JSON.parse(JSON.stringify(resolveOptions));
                let index = extractNumber(String(x?.path));
                if (typeof index !== "number") {
                  index = typeof value?.index === "number" ? value.index : null;
                }
                const _viewModel =
                  typeof index === "number" &&
                  rlViewModel[rlType as string][index];
                const requiredFields = _fields.filter(
                  (field) =>
                    field.required &&
                    !field.parentFieldId &&
                    !(isSubmittedIncident && field.requiredForNewIncidentOnly)
                );
                const staticShape = getStaticShape(_fields, requiredFields);
                return Yup.object().when([], {
                  is: () => !!_viewModel,
                  then: Yup.object(
                    getYupValidator(
                      _fields as IFieldInstance[],
                      _viewModel,
                      undefined,
                      current.fieldName,
                      isSubmittedIncident
                    )
                  ),
                  otherwise: Yup.object(staticShape as any),
                });
              }),
            })
          )
        : Yup.array().of(Yup.object({}));
      yupFieldSchema = updatedYupFieldSchema;
    }
    let updatedSchema = extendYupFieldSchema(yupFieldSchema, current);

    if (acc[current.fieldName]) {
      const fieldSchema = enforceRequired
        ? getValidationForRequired(
            updatedSchema,
            current,
            viewModel,
            rlFieldName
          )
        : updatedSchema;
      updatedSchema = Yup.mixed().when([], {
        is: () => viewModel[current.id],
        then: fieldSchema,
        otherwise: acc[current.fieldName],
      });
    } else {
      if (enforceRequired) {
        updatedSchema = getValidationForRequired(
          updatedSchema,
          current,
          viewModel,
          rlFieldName
        );
      }
    }
    acc[current.fieldName] = updatedSchema;
    return acc;
  }, {});
};
